import { CountryCodeFieldComponent } from '../../country-code-field.component';
import { Component, inject, OnInit } from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { KwdCurrencyPipe } from '../../../pipes';
import { MatIconModule } from '@angular/material/icon';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacButtonComponent,
  CacFieldComponent,
  CacTableComponent,
  CacFormBuilderComponent,
  formBuilder,
  table,
  ItemRecord,
} from '@controladad/ng-base';
import { OrderCustomerUpdate, OrderDetailEntity, OrderItemDetail, OrderItemStatus, OrderLog, UserCustomerUpdate } from '@models';
import { OrderApiService, AddressApiService, PaymentMethodApiService } from '../../../services';
import { OrderStatusValues, HouseTypeValues, OrderItemStatusValues } from '../../../data';
import { formControl, formGroup, Validators } from '@al00x/forms';
import { BehaviorSubject, switchMap, take, tap, of, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogExtendedService } from '../dialog-extended.service';


export interface OrderDetailDialogData {
  orderId: number;
  orderDetail?: OrderDetailEntity;
}

export type OrderDetailDialogResult = boolean;

@Component({
  selector: 'lib-order-detail-dialog',
  standalone: true,
  imports: [
    CacFieldComponent,
    CacButtonComponent,
    CacTableComponent,
    CacFormBuilderComponent,
    CountryCodeFieldComponent,
    MatIconModule,
    AsyncPipe,
    DatePipe,
    KwdCurrencyPipe,
  ],
  templateUrl: './order-detail-dialog.component.html',
  styleUrl: './order-detail-dialog.component.scss',
})
export class OrderDetailDialogComponent
  extends CacBaseDialogComponent<OrderDetailDialogData, OrderDetailDialogResult>
  implements OnInit {
  private orderApi = inject(OrderApiService);
  private addressApi = inject(AddressApiService);
  private paymentMethodApi = inject(PaymentMethodApiService);
  private dialogService = inject(DialogExtendedService);
  private kwdCurrencyPipe = new KwdCurrencyPipe();

  orderDetail$ = new BehaviorSubject<OrderDetailEntity | null>(null);
  areaList$ = new BehaviorSubject<ItemRecord<number>[]>([]);
  paymentMethodList$ = new BehaviorSubject<ItemRecord<number>[]>([]);
  loading = true;
  selectedTabIndex = 0;


  OrderStatusValues = OrderStatusValues;
  HouseTypeValues = HouseTypeValues;

  countryCode = formControl<string | null>(null);
  customerForm = formBuilder({
    cols: 2,
    width: '100%',
    inputs: {
      customerFullName: {
        control: formControl<string>('', [Validators.required, Validators.maxLength(257)]),
        label: 'Full Name',
      },
      customerEmail: {
        control: formControl<string>('', [Validators.email]),
        label: 'Email',
      },
      customerPhoneNumber: {
        control: formControl<string>(''),
        label: 'Phone Number',
      },
    },
  });

  addressForm = formBuilder({
    cols: 3,
    width: '100%',
    inputs: {
      governorateId: {
        control: formControl<number>(),
        label: 'Governorate',
        type: 'select',
        items: this.addressApi.getGovernorates(),
      },
      areaId: {
        control: formControl<number>(),
        label: 'Area',
        type: 'select',
        items: this.areaList$,
      },
      address: {
        control: formControl<string>('', [Validators.required, Validators.maxLength(4096)]),
        label: 'Address',
        type: 'textarea',
      },
    },
    onInit: (controls) => {
      // Listen for changes to governorate
      controls.governorateId.valueChanges
        .pipe(
          takeUntilDestroyed(),
          switchMap((value) => {
            if (!value) return of([]);
            return this.addressApi.getAreas(value).pipe(take(1));
          }),
          tap((areas) => this.areaList$.next(areas)),
        )
        .subscribe();
    },
  });

  // Order status form
  statusForm = formGroup({
    status: formControl<number>(),
  });

  // Payment method form
  paymentMethodForm = formGroup({
    paymentMethodId: formControl<number>(),
  });

  // Shipping cost form
  shippingCostForm = formGroup({
    shippingCost: formControl<number>(undefined, Validators.required),
  });

  // Order items table
  orderItemsTable = table({
    itemsFn: () => {
      return this.orderApi.getOrderDetails(this.data.orderId).pipe(
        tap((order: OrderDetailEntity) => this.orderDetail$.next(order)),
        map((order: OrderDetailEntity) => order.items || [])
      );
    },
    pagination: false,
    view: {
      title: 'Order Items',
      itemName: 'Order Item',
    },
    events: {
      add: () => {
        const orderDetail = this.orderDetail$.value;
        // Disable adding products if order is cancelled (5) or refunded (6)
        if (orderDetail?.status === 5 || orderDetail?.status === 6) {
          snackbar$.show('Cannot add products to cancelled or refunded orders');
          return;
        }

        return dialog$.orderItemAdd({
          orderId: this.data.orderId,
          existingVariantIds: this.getExistingVariantIds()
        }).afterSubmit().pipe(
          tap((result) => {
            if (result) {
              this.loadOrderDetails();
            }
          })
        );
      }
    },
    columns: {
      'productImage.url': {
        label: 'Image',
        type: 'image',
      },
      productName: {
        label: 'Product Name',
        transform: (_, item: OrderItemDetail) => this.getProductName(item),
        class: 'multiline-text',
      },
      productSku: {
        label: 'SKU',
      },
      variationTerms: {
        label: 'Variations',
        transform: (_, item: OrderItemDetail) => this.getVariationTerms(item),
      },
      orderedQuantity: {
        label: 'Quantity',
        type: 'number',
      },
      productPrice: {
        label: 'Unit Price',
        type: 'text',
        transform: (value?: number) => value ? this.kwdCurrencyPipe.transform(value) : 'Gift',
      },
      totalPrice: {
        label: 'Total Price',
        type: 'text',
        transform: (_, item: OrderItemDetail) => item.productPrice ? this.kwdCurrencyPipe.transform(item.productPrice * item.orderedQuantity) : 'Gift',
      },
      productVendorName: {
        label: 'Vendor',
      },
      variantOrderStatus: {
        label: 'Status',
        type: 'tag',
        items: OrderItemStatusValues,
      },
    },
    actions: [
      {
        type: 'button',
        content: 'Edit',
        appearance: 'stroked',
        disabled: (item: OrderItemDetail) => {
          const orderDetail = this.orderDetail$.value;
          const orderStatus = orderDetail?.status;
          // Disable if item is already cancelled, or if order is cancelled (5) or refunded (6)
          return item.variantOrderStatus === OrderItemStatus.Cancelled ||
            orderStatus === 5 ||
            orderStatus === 6;
        },
        action: (item: OrderItemDetail) => {
          return dialog$.orderItemEdit({
            orderItem: item,
            orderId: this.data.orderId,
            shouldPromptWalletRefund: this.shouldPromptWalletRefund()
          }).afterSubmit().pipe(
            tap((result) => {
              if (result) {
                this.loadOrderDetails(); // Refresh order data
              }
            })
          );
        },
      },
      {
        type: 'button',
        content: 'Cancel',
        disabled: (item: OrderItemDetail) => {
          const orderDetail = this.orderDetail$.value;
          const orderStatus = orderDetail?.status;
          // Disable if item is already cancelled, or if order is cancelled (5) or refunded (6)
          return item.variantOrderStatus === OrderItemStatus.Cancelled ||
            orderStatus === 5 ||
            orderStatus === 6;
        },
        action: (item: OrderItemDetail) => {
          const productName = this.getProductName(item);
          const orderDetail = this.orderDetail$.value!;

          // First show cancel confirmation
          return dialog$
            .prompt({
              title: 'Cancel Product',
              message: `Are you sure you want to cancel "${productName}"?`,
              yesButtonText: 'Yes, Cancel',
              noButtonText: 'No, Keep',
            })
            .action(() => {
              // Check if we should prompt for wallet refund
              if (!this.shouldPromptWalletRefund()) {
                // Don't prompt: just cancel without refund option
                return this.orderApi.cancelOrderItem(orderDetail.id, item.variantId, false).pipe(
                  tap(() => {
                    snackbar$.show('Item cancelled successfully');
                    this.loadOrderDetails(); // Reload to get updated data
                  })
                );
              }

              // Prompt user: show refund dialog
              return this.dialogService.refundConfirmation().afterClosed().pipe(
                switchMap((refundToWallet) => {
                  if (refundToWallet === true) {
                    // User chose "Yes, Refund to Wallet"
                    return this.orderApi.cancelOrderItem(orderDetail.id, item.variantId, true).pipe(
                      tap(() => {
                        snackbar$.show('Item cancelled successfully');
                        this.loadOrderDetails(); // Reload to get updated data
                      })
                    );
                  } else if (refundToWallet === false) {
                    // User chose "No, Just Update"
                    return this.orderApi.cancelOrderItem(orderDetail.id, item.variantId, false).pipe(
                      tap(() => {
                        snackbar$.show('Item cancelled successfully');
                        this.loadOrderDetails(); // Reload to get updated data
                      })
                    );
                  } else {
                    // User closed the dialog (refundToWallet === null) - return empty observable to cancel
                    return of(null);
                  }
                })
              );
            });
        },
      },
    ],
  });

  // Order logs table
  orderLogsTable = table({
    itemsFn: () => {
      return this.orderApi.getOrderDetails(this.data.orderId).pipe(
        tap((order: OrderDetailEntity) => this.orderDetail$.next(order)),
        map((order: OrderDetailEntity) => order.logs || [])
      );
    },
    pagination: false,
    view: {
      title: 'Order Activity Logs',
    },
    columns: {
      date: {
        label: 'Date & Time',
        type: 'datetime',
      },
      from: {
        label: 'From',
        class: 'multiline-text',
      },
      to: {
        label: 'To',
        class: 'multiline-text',
      },
      action: {
        label: 'Action',
        class: 'multiline-text',
      },
      user: { label: 'User' },
    },
  });

  ngOnInit() {
    // Load payment methods
    this.loadPaymentMethods();

    // If order detail is pre-loaded, use it; otherwise load it
    if (this.data.orderDetail) {
      this.orderDetail$.next(this.data.orderDetail);
      this.populateForms(this.data.orderDetail);
      this.loading = false;
    } else {
      this.loadOrderDetails();
    }
  }

  private loadPaymentMethods() {
    this.paymentMethodApi.getAll().pipe(take(1)).subscribe({
      next: (methods) => {
        const paymentMethodItems = methods
          .filter(method => method.status === 1) // Only active payment methods
          .map(method => ({
            label: method.name.english || method.name.arabic || 'Unknown',
            value: method.id
          }));
        this.paymentMethodList$.next(paymentMethodItems);
      },
      error: (error) => {
        console.error('Error loading payment methods:', error);
      }
    });
  }

  onUpdatePaymentMethod(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    const newPaymentMethodId = this.paymentMethodForm.controls.paymentMethodId.value;

    if (!orderDetail || !newPaymentMethodId || newPaymentMethodId === orderDetail.paymentMethodId) {
      e.setLoading(false);
      return;
    }

    this.orderApi.updateOrderPaymentMethod(orderDetail.id, newPaymentMethodId)
      .pipe(e.pipe())
      .subscribe({
        next: () => {
          snackbar$.show('Payment method updated successfully');
          this.loadOrderDetails(); // Refresh order details
        },
        error: (error) => {
          console.error('Error updating payment method:', error);
          snackbar$.show('Failed to update payment method');
        }
      });
  }

  onUpdateShippingCost(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    const newShippingCost = this.shippingCostForm.controls.shippingCost.value;

    if (!orderDetail || newShippingCost === undefined || newShippingCost === orderDetail.prices?.shippingFee) {
      e.setLoading(false);
      return;
    }

    if (!this.shippingCostForm.valid) {
      this.shippingCostForm.markAllAsTouched();
      e.setLoading(false);
      return;
    }

    this.orderApi.updateOrderCustomerInfo({
      orderId: orderDetail.id,
      fields: {
        shippingCost: newShippingCost,
      },
    })
      .pipe(e.pipe())
      .subscribe({
        next: () => {
          snackbar$.show('Shipping cost updated successfully');
          this.loadOrderDetails(); // Refresh order details
        },
        error: (error) => {
          console.error('Error updating shipping cost:', error);
          snackbar$.show('Failed to update shipping cost');
        }
      });
  }

  private loadOrderDetails() {
    this.loading = true;
    this.orderApi.getOrderDetails(this.data.orderId).subscribe({
      next: (orderDetail) => {
        this.orderDetail$.next(orderDetail);
        this.populateForms(orderDetail);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading order details:', error);
        this.loading = false;
        snackbar$.show('Error loading order details');
      },
    });
  }

  private populateForms(orderDetail: OrderDetailEntity) {
    this.countryCode.setValue(orderDetail.customerCountryCode ?? null);
    // Populate customer form
    this.customerForm.patchValue({
      customerFullName: orderDetail.customerFullName || '',
      customerEmail: orderDetail.customerEmail || '',
      customerPhoneNumber: orderDetail.customerPhoneNumber || '',
    });

    // Populate address form - with null checks
    if (orderDetail.address) {
      this.addressForm.patchValue({
        governorateId: orderDetail.address.governorateId,
        areaId: orderDetail.address.areaId,
        address: orderDetail.address.address || '',
      });

      // Load areas for the selected governorate
      if (orderDetail.address.governorateId) {
        this.addressApi.getAreas(orderDetail.address.governorateId)
          .pipe(take(1))
          .subscribe(areas => this.areaList$.next(areas));
      }
    }

    // Populate status form
    this.statusForm.patchValue({
      status: orderDetail.status,
    });

    // Enable/disable status control based on order status
    if (this.isStatusDropdownDisabled(orderDetail.status)) {
      this.statusForm.controls.status.disable();
    } else {
      this.statusForm.controls.status.enable();
    }

    // Populate payment method form
    this.paymentMethodForm.patchValue({
      paymentMethodId: orderDetail.paymentMethodId,
    });

    // Enable/disable payment method control based on order status
    // Disable for Cancelled (5) and Refunded (6)
    if (orderDetail.status === 5 || orderDetail.status === 6) {
      this.paymentMethodForm.controls.paymentMethodId.disable();
    } else {
      this.paymentMethodForm.controls.paymentMethodId.enable();
    }

    // Populate shipping cost form
    this.shippingCostForm.patchValue({
      shippingCost: orderDetail.prices?.shippingFee || 0,
    });

    // Enable/disable shipping cost control based on order status
    // Disable for Cancelled (5) and Refunded (6)
    if (orderDetail.status === 5 || orderDetail.status === 6) {
      this.shippingCostForm.controls.shippingCost.disable();
    } else {
      this.shippingCostForm.controls.shippingCost.enable();
    }

  }

  // Check if status change is allowed
  isStatusChangeAllowed(currentStatus: number, newStatus: number): boolean {
    // No order can change its status to Pending Payment (0)
    if (newStatus === 0) {
      return false;
    }

    // Pending Payment (0) can only be changed to Completed (4)
    if (currentStatus === 0 && newStatus !== 4) {
      return false;
    }

    // Cancelled (5) and Refunded (6) cannot be changed
    if (currentStatus === 5 || currentStatus === 6) {
      return false;
    }

    // Completed orders (4) can only change to Refunded (6)
    if (currentStatus === 4 && newStatus !== 6) {
      return false;
    }

    return true;
  }

  // Check if status dropdown should be disabled
  isStatusDropdownDisabled(currentStatus: number): boolean {
    // Disable dropdown for Pending Payment, Cancelled, and Refunded
    return currentStatus === 0 || currentStatus === 5 || currentStatus === 6;
  }

  // Get payment method options
  get paymentMethodOptions(): ItemRecord<number>[] {
    return this.paymentMethodList$.value || [];
  }

  // Get filtered status options based on current status
  getFilteredStatusOptions(currentStatus: number): ItemRecord<number>[] {
    // Define allowed transitions for each status
    const allowedTransitions: Record<number, number[]> = {
      0: [0], // Pending Payment: can only stay as Pending Payment (read-only)
      1: [1, 2, 3, 4, 5], // Pending: can go to any status except Pending Payment
      2: [2, 3, 4, 5], // Processing: can go to Ready, Completed, Cancelled
      3: [3, 4, 5], // Ready: can go to Completed, Cancelled
      4: [4, 6], // Completed: can only go to Refunded (+ show current)
      5: [5], // Cancelled: cannot be changed (read-only)
      6: [6], // Refunded: cannot be changed (read-only)
    };

    const allowedStatusValues = allowedTransitions[currentStatus] || [];

    return OrderStatusValues.filter(status =>
      allowedStatusValues.includes(status.value)
    );
  }

  // Tab management
  selectTab(index: number) {
    this.selectedTabIndex = index;
  }

  // Refresh data method


  // Action methods
  onUpdateStatus(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    const newStatus = this.statusForm.controls.status.value;

    if (!orderDetail || !newStatus || newStatus === orderDetail.status) {
      return;
    }

    if (!this.isStatusChangeAllowed(orderDetail.status, newStatus)) {
      snackbar$.show('This status change is not allowed');
      return;
    }

    // Only show refund dialog when changing status to Cancelled (5)
    if (newStatus === 5) {
      // Check if we should prompt for wallet refund
      if (!this.shouldPromptWalletRefund()) {
        // Don't prompt: just cancel without refund option
        this.orderApi.updateOrderStatus(orderDetail.id, newStatus, false)
          .pipe(e.pipe())
          .subscribe({
            next: () => {
              snackbar$.show('Order status updated successfully');
              this.loadOrderDetails(); // Reload to get updated data
            },
            error: (error) => {
              console.error('Error updating order status:', error);
              snackbar$.show('Error updating order status');
            }
          });
      } else {
        // Prompt user: show refund dialog
        this.dialogService.refundConfirmation().afterClosed().subscribe((refundToWallet) => {
          if (refundToWallet === true) {
            this.orderApi.updateOrderStatus(orderDetail.id, newStatus, true)
              .pipe(e.pipe())
              .subscribe({
                next: () => {
                  snackbar$.show('Order status updated successfully');
                  this.loadOrderDetails();
                },
                error: (error) => {
                  console.error('Error updating order status:', error);
                  snackbar$.show('Error updating order status');
                }
              });
          } else if (refundToWallet === false) {
            // User chose "No, Just Update"
            this.orderApi.updateOrderStatus(orderDetail.id, newStatus, false)
              .pipe(e.pipe())
              .subscribe({
                next: () => {
                  snackbar$.show('Order status updated successfully');
                  this.loadOrderDetails();
                },
                error: (error) => {
                  console.error('Error updating order status:', error);
                  snackbar$.show('Error updating order status');
                }
              });
          } else {
            e.setLoading(false);
          }
        });
      }
    } else {
      // For other status changes, don't show refund dialog
      this.orderApi.updateOrderStatus(orderDetail.id, newStatus, false)
        .pipe(e.pipe())
        .subscribe({
          next: () => {
            snackbar$.show('Order status updated successfully');
            this.loadOrderDetails();
          },
          error: (error) => {
            console.error('Error updating order status:', error);
            snackbar$.show('Error updating order status');
          }
        });
    }
  }

  onDeleteOrder(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    if (!orderDetail) return;

    dialog$
      .deletePrompt(false, { itemName: 'Order' })
      .action(() => this.orderApi.delete(orderDetail.id))
      .pipe(e.pipe())
      .subscribe(() => {
        snackbar$.show('Order deleted successfully');
        this.submit(true);
      });
  }

  onDownloadPdf(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    if (!orderDetail) return;

    // First show the invoice date dialog
    this.dialogService.invoiceDate({ invoiceDate: new Date(orderDetail.orderDate) }).afterClosed().subscribe((result) => {
      if (result !== null && result !== undefined) {
        // User clicked Download (result contains { overrideDate: Date | null })
        this.orderApi.downloadInvoice(orderDetail.id, result.overrideDate)
          .pipe(e.pipe())
          .subscribe(() => {
            snackbar$.show('Invoice download started');
          });
      } else {
        // User cancelled
        e.setLoading(false);
      }
    });
  }

  onSendOrderToErp(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    if (!orderDetail) {
      e.setLoading(false);
      return;
    }

    this.orderApi.syncOrderWithErp(orderDetail.id)
      .pipe(e.pipe())
      .subscribe({
        next: () => {
          snackbar$.show('Order submitted to ERP');
          this.loadOrderDetails();
        },
        error: (error) => {
          console.error('Error submitting order to ERP:', error);
          snackbar$.show(error?.error?.message ?? 'Failed to submit order to ERP');
        }
      });
  }

  onUpdateCustomerInfo(e: ButtonClickEvent) {
    const orderDetail = this.orderDetail$.value;
    if (!orderDetail) return;

    const customerValues = this.customerForm.getValue();
    const addressValues = this.addressForm.getValue();

    const updateData: OrderCustomerUpdate = {
      orderId: orderDetail.id,
      fields: {
        email: customerValues?.customerEmail,
        fullName: customerValues?.customerFullName,
        countryCode: this.countryCode.value,
        phoneNumber: customerValues?.customerPhoneNumber,
        governorateId: addressValues?.governorateId,
        areaId: addressValues?.areaId,
        address: addressValues?.address,
      },
    };

    this.orderApi.updateOrderCustomerInfo(updateData)
      .pipe(e.pipe())
      .subscribe(() => {
        snackbar$.show('Customer information updated successfully');
        this.loadOrderDetails(); // Reload to get updated data
      });
  }

  getPaymentMethodName(paymentMethod?: any): string {
    if (!paymentMethod) return 'N/A';
    if (typeof paymentMethod === 'string') return paymentMethod;
    if (paymentMethod && typeof paymentMethod === 'object') {
      return paymentMethod.english || paymentMethod.arabic || 'N/A';
    }
    return 'N/A';
  }

  shouldPromptWalletRefund(): boolean {
    const orderDetail = this.orderDetail$.value;
    return orderDetail?.shouldPromptWalletRefund ?? !orderDetail?.isGuest;
  }

  getProductName(item: OrderItemDetail): string {
    if (!item.productName) return '-';
    if (typeof item.productName === 'string') return item.productName;
    if (typeof item.productName === 'object') {
      return item.productName.english || item.productName.arabic || '-';
    }
    return '-';
  }

  getVariationTerms(item: OrderItemDetail): string {
    if (!item.variationTerms || !Array.isArray(item.variationTerms)) return '-';

    const terms = item.variationTerms.map((variation: any) => {
      if (variation.term) {
        return variation.term.english || variation.term.arabic || '';
      }
      return '';
    }).filter(term => term.trim() !== '');

    return terms.length > 0 ? terms.join(', ') : '-';
  }

  getExistingVariantIds(): number[] {
    const orderDetail = this.orderDetail$.value;
    if (!orderDetail || !orderDetail.items) {
      return [];
    }
    return orderDetail.items.map(item => item.variantId);
  }

  // Close dialog method
  closeDialog(): void {
    this.submit(true); // Always return true to reload parent data
  }
}
